import React, { useState, useEffect } from "react";
import { ShoppingCart, Heart, Star, Search, ArrowRight, Play, Crown, Flame, Eye, TrendingUp, Truck, Shield, Zap, Grid, List } from "lucide-react";
import { Input } from "./ui/input";

// Mock products as fallback - defined outside component to prevent re-renders
const MOCK_PRODUCTS = [
  {
    id: 1,
    name: "888 PORTAL",
    subtitle: "TRANSFORMATION HOODIE",
    price: 188,
    originalPrice: 225,
    category: "Hoodies",
    image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=800&h=1000&fit=crop&auto=format",
    hoverImage: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=1000&fit=crop&auto=format",
    colors: ["SACRED GOLD", "DIVINE PURPLE", "FIRE ORANGE"],
    sizes: ["XS", "S", "M", "L", "XL", "XXL"],
    rating: 4.9,
    reviews: 1888,
    bestseller: true,
    trending: true,
    stock: "ALMOST SOLD OUT",
    description: "CHANNEL DIVINE FREQUENCY THROUGH SACRED NUMEROLOGY"
  },
  {
    id: 2,
    name: "FLY FREQUENCY Signature Tee",
    price: 44.44,
    originalPrice: 55.55,
    category: "T-Shirts", 
    image: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=500&h=600&fit=crop",
    colors: ["Cosmic Black", "Divine White", "Sacred Red"],
    sizes: ["XS", "S", "M", "L", "XL", "XXL"],
    rating: 4.8,
    reviews: 777,
    newArrival: true,
    description: "Faith.Love.Yah frequency activator. Raises your vibration with every wear.",
    features: ["Breathable Fabric", "Fade Resistant", "Comfort Fit", "Spiritual Activation"]
  },
  {
    id: 3,
    name: "A.R.T. ENERGI Snapback",
    price: 33.33,
    category: "Accessories",
    image: "https://images.unsplash.com/photo-1588850561407-ed78c282e89b?w=500&h=600&fit=crop",
    colors: ["Divine Black", "Sacred Gold", "Fire Red"],
    rating: 4.7,
    reviews: 555,
    description: "Authenticity, Reliability, Truth crown piece. Wear your spiritual authority.",
    features: ["Adjustable Fit", "Premium Materials", "Sacred Symbols", "Universal Appeal"]
  },
];

const InspiyahStore = () => {
  console.log("InspiyahStore component is rendering"); // Debug log
  
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [cartCount, setCartCount] = useState(0);
  const [favorites, setFavorites] = useState(new Set());
  const [currentHero, setCurrentHero] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [products, setProducts] = useState(MOCK_PRODUCTS); // Initialize with mock products
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setIsLoaded(true);
    const interval = setInterval(() => {
      setCurrentHero(prev => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Fetch products from Shopify via backend
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const backendUrl = '';
        console.log('🔍 Backend URL from env:', backendUrl);
        console.log('🔍 Full API URL:', `${backendUrl}/api/shopify/products`);
        
        const response = await fetch(`${backendUrl}/api/shopify/products`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch products: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('✅ Products received from Shopify:', data.count);
        setProducts(data.products || []);
      } catch (error) {
        console.error('❌ Error fetching products:', error);
        console.log('⚠️ Falling back to mock data');
        // Fallback to mock data if API fails
        setProducts(MOCK_PRODUCTS);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Mock products as fallback
  const categories = ["All", "T-Shirts", "Hoodies", "Bottoms", "Outerwear", "Accessories", "Jewelry"];

  const filteredProducts = selectedCategory === "All" 
    ? products 
    : products.filter(product => {
        // Match category or check if product name/description contains the category
        const categoryLower = selectedCategory.toLowerCase();
        const nameCheck = product.name.toLowerCase().includes(categoryLower);
        const descCheck = product.description?.toLowerCase().includes(categoryLower) || false;
        const categoryCheck = product.category?.toLowerCase() === categoryLower;
        return categoryCheck || nameCheck || descCheck;
      });

  const addToCart = (productId) => {
    setCartCount(cartCount + 1);
    console.log(`Added product ${productId} to cart`);
  };

  const toggleFavorite = (productId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(productId)) {
      newFavorites.delete(productId);
    } else {
      newFavorites.add(productId);
    }
    setFavorites(newFavorites);
  };

  const heroSlides = [
    {
      title: "INSPIYAH CULTURE",
      subtitle: "WHERE SPIRIT MEETS STREET",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1920&h=1080&fit=crop&auto=format",
      accent: "888"
    },
    {
      title: "FLY FREQUENCY",
      subtitle: "FAITH • LOVE • YAH",
      image: "https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=1920&h=1080&fit=crop&auto=format", 
      accent: "1010"
    },
    {
      title: "A.R.T. ENERGI",
      subtitle: "AUTHENTICITY • RELIABILITY • TRUTH",
      image: "https://images.unsplash.com/photo-1509631179647-0177331693ae?w=1920&h=1080&fit=crop&auto=format",
      accent: "777"
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen">
      {/* HERO SECTION */}
      <section className="relative h-screen overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1000 ${
              currentHero === index ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60 z-10" />
            <img 
              src={slide.image} 
              alt={slide.title}
              className="w-full h-full object-cover"
            />
          </div>
        ))}

        {/* Hero Content */}
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <div className="text-center px-6 max-w-6xl mx-auto">
            <div className={`transform transition-all duration-1000 delay-300 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
              <div className="text-8xl md:text-[12rem] lg:text-[16rem] font-black leading-none tracking-tighter mb-4">
                <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent animate-pulse">
                  {heroSlides[currentHero].accent}
                </span>
              </div>
              
              <h1 className="text-4xl md:text-6xl lg:text-8xl font-black tracking-wider mb-6">
                <span className="bg-gradient-to-r from-white via-gray-200 to-white bg-clip-text text-transparent">
                  {heroSlides[currentHero].title}
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl lg:text-3xl font-light tracking-[0.5em] text-gray-300 mb-12">
                {heroSlides[currentHero].subtitle}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <button 
                  onClick={(e) => {
                    console.log('🔴 SHOP NOW CLICKED!', e);
                    const productsSection = document.getElementById('products-section');
                    console.log('🔍 Products section element:', productsSection);
                    if (productsSection) {
                      console.log('✅ Scrolling to products...');
                      productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    } else {
                      console.log('❌ Products section not found in DOM!');
                    }
                  }}
                  className="group bg-white text-black px-12 py-6 text-xl font-bold tracking-wider hover:bg-yellow-400 transition-all duration-300 transform hover:scale-105 hover:rotate-1 relative overflow-hidden"
                >
                  <span className="relative z-10">SHOP NOW</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>
                
                <button className="group flex items-center space-x-4 text-white border-2 border-white px-8 py-6 text-xl font-light tracking-wider hover:bg-white hover:text-black transition-all duration-300">
                  <Play size={24} className="group-hover:scale-110 transition-transform" />
                  <span>WATCH MANIFESTO</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Navigation Dots */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-30 flex space-x-4">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentHero(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                currentHero === index ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      </section>

      {/* STICKY NAVIGATION */}
      <nav className="sticky top-0 z-50 bg-black/95 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-6">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="text-2xl font-black tracking-wider">
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                INSPIYAH
              </span>
            </div>

            {/* Categories */}
            <div className="hidden md:flex space-x-8">
              {["ALL", "HOODIES", "TEES", "BOTTOMS", "ACCESSORIES", "JEWELRY"].map((cat) => {
                const categoryValue = cat === "ALL" ? "All" : 
                                     cat === "TEES" ? "T-Shirts" : 
                                     cat.charAt(0) + cat.slice(1).toLowerCase();
                return (
                  <button
                    key={cat}
                    onClick={(e) => {
                      console.log('🔴 CATEGORY CLICKED:', cat, categoryValue);
                      setSelectedCategory(categoryValue);
                      // Scroll to products section
                      const productsSection = document.getElementById('products-section');
                      console.log('🔍 Products section:', productsSection);
                      if (productsSection) {
                        console.log('✅ Scrolling to products...');
                        productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      } else {
                        console.log('❌ Products section not found!');
                      }
                    }}
                    className={`text-lg font-light tracking-wider transition-all duration-300 relative group ${
                      selectedCategory === categoryValue
                        ? 'text-yellow-400' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {cat}
                    <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full" />
                  </button>
                );
              })}
            </div>

            {/* Cart */}
            <button className="relative group">
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-4 rounded-full group-hover:scale-110 transition-transform duration-300">
                <ShoppingCart size={24} className="text-black" />
              </div>
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-sm w-6 h-6 rounded-full flex items-center justify-center font-bold animate-bounce">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-8xl mx-auto px-6 lg:px-12 py-16 relative z-10">
        {/* Store Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <Crown size={32} className="text-yellow-400" />
            <h1 className="text-6xl lg:text-7xl font-light tracking-tight">
              <span className="bg-gradient-to-r from-yellow-300 via-orange-400 to-red-500 bg-clip-text text-transparent">
                INSPIYAH CULTURE
              </span>
            </h1>
            <Crown size={32} className="text-orange-400" />
          </div>
          
          <p className="text-2xl font-light mb-4">
            <span className="bg-gradient-to-r from-purple-300 to-orange-300 bg-clip-text text-transparent">
              Universal Spiritual Streetwear
            </span>
          </p>
          
          <div className="text-lg text-orange-200 max-w-4xl mx-auto font-light leading-relaxed mb-8">
            Where YAH Spirit meets Street Culture. Every piece carries sacred activation energy. 
            <span className="italic"> Drip to sculpture INSPIYAH Culture.</span>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-300">
            <div className="flex items-center space-x-2">
              <Truck size={16} className="text-green-400" />
              <span>Free Shipping Worldwide</span>
            </div>
            <div className="flex items-center space-x-2">
              <Shield size={16} className="text-blue-400" />
              <span>Sacred Guarantee</span>
            </div>
            <div className="flex items-center space-x-2">
              <Zap size={16} className="text-yellow-400" />
              <span>Spiritual Activation Guaranteed</span>
            </div>
          </div>
        </div>

        {/* Navigation Bar */}
        <div className="bg-black/60 backdrop-blur-lg rounded-2xl border border-yellow-400/20 p-6 mb-12 sticky top-24 z-40">
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input 
                placeholder="Search sacred streetwear..."
                className="pl-12 bg-white/10 border-gray-600 text-white placeholder-gray-400 focus:border-yellow-400"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-light transition-all duration-300 ${
                    selectedCategory === category
                      ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-black shadow-lg"
                      : "bg-white/10 text-gray-300 hover:bg-yellow-400/20 border border-gray-600"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* View Toggle & Cart */}
            <div className="flex items-center space-x-4">
              <div className="flex bg-white/10 rounded-lg p-1">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded ${viewMode === "grid" ? "bg-yellow-400 text-black" : "text-gray-400"}`}
                >
                  <Grid size={16} />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded ${viewMode === "list" ? "bg-yellow-400 text-black" : "text-gray-400"}`}
                >
                  <List size={16} />
                </button>
              </div>

              <button className="relative bg-gradient-to-r from-yellow-400 to-orange-500 text-black p-3 rounded-full hover:scale-110 transition-transform duration-300 shadow-lg">
                <ShoppingCart size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* PRODUCTS SECTION */}
        <section id="products-section" className="py-24">
          {console.log('✅ PRODUCTS SECTION RENDERING. Products count:', products.length, 'Loading:', loading)}
          <div className="text-center mb-16">
            <h2 className="text-6xl md:text-8xl font-black tracking-wider mb-6">
              <span className="bg-gradient-to-r from-gray-100 via-white to-gray-100 bg-clip-text text-transparent">
                COLLECTIONS
              </span>
            </h2>
            <p className="text-xl text-gray-400 font-light tracking-wider">
              EVERY PIECE CARRIES SACRED ACTIVATION ENERGY
            </p>
          </div>

          {/* Featured Product Showcase */}
          {products.length > 0 && (
            <div className="mb-24">
              <div className="relative group cursor-pointer">
                <div className="aspect-[16/9] bg-gradient-to-r from-gray-900 to-black rounded-3xl overflow-hidden">
                  {products[0].image ? (
                    <img 
                      src={products[0].image}
                      alt={products[0].name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=1920&h=1080&fit=crop&auto=format";
                      }}
                    />
                  ) : (
                    <img 
                      src="https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=1920&h=1080&fit=crop&auto=format"
                      alt="Featured Product"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute bottom-12 left-12 right-12">
                    <div className="flex items-end justify-between">
                      <div>
                        <div className="text-yellow-400 text-lg font-light tracking-[0.3em] mb-2">FEATURED</div>
                        <h3 className="text-5xl md:text-7xl font-black tracking-wider mb-4">{products[0].name.toUpperCase()}</h3>
                        <p className="text-xl text-gray-300 font-light tracking-wider mb-6">{products[0].description || "SACRED ACTIVATION ENERGY"}</p>
                        <button 
                          onClick={() => {
                            const firstProduct = document.querySelector(`[data-product-id="${products[0].id}"]`);
                            if (firstProduct) {
                              firstProduct.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }}
                          className="bg-white text-black px-8 py-4 font-bold tracking-wider hover:bg-yellow-400 transition-colors duration-300"
                        >
                          SHOP NOW - ${products[0].price}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Products Grid */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-yellow-400"></div>
              <p className="mt-4 text-gray-400">Loading sacred products...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-2xl text-gray-400 mb-4">No products found in this category</p>
              <button 
                onClick={() => setSelectedCategory("All")}
                className="bg-yellow-400 text-black px-8 py-3 font-bold tracking-wider hover:bg-orange-500 transition-colors duration-300"
              >
                VIEW ALL PRODUCTS
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
              <div 
                key={product.id}
                data-product-id={product.id}
                className="group relative bg-gray-900 rounded-2xl overflow-hidden hover:-translate-y-4 transition-all duration-500"
              >
                {/* Product Image */}
                <div className="relative aspect-[3/4] overflow-hidden">
                  {product.image ? (
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=800&h=1000&fit=crop&auto=format";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-800 text-gray-500">
                      No Image
                    </div>
                  )}
                  
                  {/* Hover Image */}
                  {product.hoverImage && (
                    <img 
                      src={product.hoverImage}
                      alt={product.name}
                      className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Status Badges */}
                  <div className="absolute top-6 left-6 flex flex-col space-y-2">
                    {product.trending && (
                      <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-3 py-1 text-xs font-black tracking-wider flex items-center space-x-1">
                        <TrendingUp size={12} />
                        <span>TRENDING</span>
                      </div>
                    )}
                    {product.bestseller && (
                      <div className="bg-white text-black px-3 py-1 text-xs font-black tracking-wider flex items-center space-x-1">
                        <Crown size={12} />
                        <span>BESTSELLER</span>
                      </div>
                    )}
                    {product.stock && (
                      <div className="bg-red-500 text-white px-3 py-1 text-xs font-black tracking-wider animate-pulse">
                        {product.stock}
                      </div>
                    )}
                  </div>

                  {/* Quick Actions */}
                  <div className="absolute top-6 right-6 flex flex-col space-y-3 transform translate-x-12 group-hover:translate-x-0 transition-transform duration-500">
                    <button
                      onClick={() => toggleFavorite(product.id)}
                      className={`w-12 h-12 rounded-full backdrop-blur-md transition-all duration-300 flex items-center justify-center ${
                        favorites.has(product.id) 
                          ? "bg-red-500 text-white scale-110" 
                          : "bg-black/50 text-white hover:bg-red-500 hover:scale-110"
                      }`}
                    >
                      <Heart size={18} fill={favorites.has(product.id) ? "currentColor" : "none"} />
                    </button>
                  </div>

                  {/* Quick Add to Cart */}
                  <div className="absolute bottom-6 left-6 right-6 transform translate-y-12 group-hover:translate-y-0 transition-transform duration-500 delay-100">
                    <button 
                      onClick={() => addToCart(product.id)}
                      className="w-full bg-white text-black py-4 font-black tracking-wider hover:bg-yellow-400 transition-all duration-300 hover:scale-105"
                    >
                      ADD TO CART - ${product.price}
                    </button>
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-8 space-y-6">
                  {/* Category */}
                  <div className="text-xs font-light tracking-[0.3em] text-gray-500 uppercase">
                    {product.category}
                  </div>

                  {/* Name */}
                  <div>
                    <h3 className="text-2xl font-black tracking-wider leading-tight mb-2">
                      {product.name}
                    </h3>
                    {product.subtitle && (
                      <p className="text-lg font-light tracking-wider text-gray-400">
                        {product.subtitle}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-gray-300 font-light tracking-wide leading-relaxed">
                    {product.description}
                  </p>

                  {/* Colors */}
                  <div className="space-y-3">
                    <div className="text-sm font-light tracking-wider text-gray-400">COLORS</div>
                    <div className="flex space-x-3">
                      {product.colors.map((color, index) => (
                        <button
                          key={index}
                          className="group relative"
                        >
                          <div 
                            className="w-8 h-8 rounded-full border-2 border-gray-600 group-hover:border-white transition-colors duration-300"
                            style={{
                              background: color.includes("GOLD") ? "#FFD700" :
                                        color.includes("PURPLE") ? "#8B5CF6" :
                                        color.includes("ORANGE") ? "#FB923C" :
                                        color.includes("RED") ? "#EF4444" :
                                        color.includes("BLACK") ? "#000000" :
                                        color.includes("WHITE") ? "#FFFFFF" : "#6B7280"
                            }}
                          />
                          <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                            {color}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          size={16} 
                          className={`${i < Math.floor(product.rating) ? "text-yellow-400 fill-current" : "text-gray-600"}`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-400 font-light">
                      {product.rating} ({product.reviews.toLocaleString()} reviews)
                    </span>
                  </div>

                  {/* Price */}
                  <div className="flex items-center space-x-4">
                    <span className="text-3xl font-black tracking-wider text-white">
                      ${product.price}
                    </span>
                    {product.originalPrice && (
                      <span className="text-lg text-gray-500 line-through font-light">
                        ${product.originalPrice}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </section>

        {/* MANIFESTO SECTION */}
        <section className="py-32 bg-gradient-to-r from-gray-900 to-black">
          <div className="text-center max-w-6xl mx-auto">
            <div className="text-8xl md:text-[12rem] font-black opacity-10 mb-8">
              YHWH יהוה
            </div>
            <h2 className="text-4xl md:text-6xl font-black tracking-wider mb-8">
              <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                UNIVERSAL MOVEMENT
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 font-light tracking-wider leading-relaxed mb-12 max-w-4xl mx-auto">
              "MY BLOOD IS RED AND WE RELATE MORE THAN WE DISAGREE"
              <br />
              EVERY PIECE CARRIES THE FREQUENCY OF TRANSFORMATION
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-12 py-6 text-xl font-black tracking-wider hover:scale-105 transition-transform duration-300">
                JOIN THE MOVEMENT
              </button>
              <button className="border-2 border-white text-white px-12 py-6 text-xl font-light tracking-wider hover:bg-white hover:text-black transition-all duration-300">
                CREATE CUSTOM
              </button>
            </div>

            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
              <div>
                <div className="text-5xl font-black mb-4">888</div>
                <div className="text-lg font-light tracking-wider text-gray-400">TRANSFORMATION PORTAL</div>
              </div>
              <div>
                <div className="text-5xl font-black mb-4">1010</div>
                <div className="text-lg font-light tracking-wider text-gray-400">CONSCIOUSNESS ELEVATION</div>
              </div>
              <div>
                <div className="text-5xl font-black mb-4">777</div>
                <div className="text-lg font-light tracking-wider text-gray-400">SPIRITUAL ALIGNMENT</div>
              </div>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="border-t border-gray-800 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <h3 className="text-3xl font-black tracking-wider mb-6">
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                  INSPIYAH CULTURE
                </span>
              </h3>
              <p className="text-gray-400 font-light tracking-wide leading-relaxed max-w-md">
                Where YAH Spirit meets Street Culture. The first universal spiritual streetwear brand 
                uniting humanity through sacred fashion.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-bold tracking-wider mb-6">COLLECTIONS</h4>
              <div className="space-y-3 text-gray-400 font-light">
                <div>HOODIES</div>
                <div>TEES</div>
                <div>BOTTOMS</div>
                <div>ACCESSORIES</div>
                <div>JEWELRY</div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-bold tracking-wider mb-6">CONNECT</h4>
              <div className="space-y-3 text-gray-400 font-light">
                <div>INSTAGRAM</div>
                <div>TWITTER</div>
                <div>YOUTUBE</div>
                <div>TIKTOK</div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-500 font-light tracking-wider">
              © 2024 INSPIYAH CULTURE. ALL RIGHTS RESERVED. | <strong>YAH HEALTH WEALTH HAPPINESS</strong>
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default InspiyahStore;